import _debounce from 'lodash/debounce';
import { bool, func, number, object, oneOfType, string } from 'prop-types';
import React from 'react';
import Autosuggest from 'react-autosuggest';
import styled from 'styled-components';

import { callFunc } from 'ravenjs/utils/actions';
import { get } from 'ravenjs/utils/lodash';
import Input from 'ravenjs/lib/Input';

import { SearchIcon } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';

import TypeAheadContainer from './TypeAheadContainer';
import TypeAheadSuggestion from './TypeAheadSuggestion';

export const InputContainer = styled.div`
    border: 1px solid ${DEFAULT_COLORS.PLACEHOLDER};
    position: relative;
    width: ${({ containerWidth }) => containerWidth};
`;

const InputStyled = styled(Input)`
    border-radius: 0;
    border-width: 0;
    color: ${DEFAULT_COLORS.BLACK};
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0.625rem 3.25rem 0.563rem 0.875rem;
    :focus {
        box-shadow: none;
    }
    color: ${props => (props.disabled ? DEFAULT_COLORS.LIGHT : DEFAULT_COLORS.BLACK)};
    ::placeholder {
        color: ${props => (props.disabled ? DEFAULT_COLORS.LIGHT : DEFAULT_COLORS.PLACEHOLDER)};
    }
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    position: absolute;
    right: 0;
    top: 0;
    padding: 0 0.781rem;
    height: 100%;
`;

const TypeAheadWrapper = styled.div`
    margin: 0;
    padding: 0;
`;

const TypeAheadFilter = ({
    asyncCallback,
    containerWidth,
    disabled,
    id,
    name,
    onChange: onChangeSelection,
    placeholder,
    value,
    showOnMouseLeaveEvent,
}) => {
    const [inputValue, setInputValue] = React.useState(get(value, 'name', ''));
    const [isLoadingSuggestions, setIsLoadingSuggestions] = React.useState(false);
    const [isNoSuggestionFound, setIsNoSuggestionFound] = React.useState(false);

    const [showTypeAheadContainer, setShowTypeAheadContainer] = React.useState(true);
    const [suggestions, setSuggestions] = React.useState([]);

    React.useEffect(() => {
        if (!value) {
            setInputValue('');
        } else if (value.name) {
            setInputValue(value.name);
        }
    }, [value]);

    const getSuggestionValue = suggestion => {
        const suggestionValue = get(suggestion, 'name', '');

        return suggestionValue;
    };

    const onChangeInput = (event, { newValue }) => {
        setInputValue(newValue);
    };

    const onSuggestionSelected = (event, selectedSuggestion) => {
        const selectedSuggestionName = get(selectedSuggestion, 'suggestion.name', null);
        const selectedSuggestionValue = get(selectedSuggestion, 'suggestion.value', null);

        callFunc(onChangeSelection, name, {
            name: selectedSuggestionName,
            value: selectedSuggestionValue,
        });
    };

    const onSuggestionsClearRequested = () => {
        setSuggestions([]);
        setIsNoSuggestionFound(false);
    };

    const loadSuggestions = value => {
        setIsLoadingSuggestions(true);
        asyncCallback(value)
            .then(updatedOptions => {
                setSuggestions(updatedOptions);
                setIsNoSuggestionFound(!updatedOptions.length);
            })
            .catch(() => {
                setSuggestions([]);
                setIsNoSuggestionFound(false);
            })
            .finally(() => {
                setIsLoadingSuggestions(false);
            });
    };

    const debouncedLoadSuggestions = React.useRef(
        _debounce(value => {
            loadSuggestions(value);
        }, 500)
    ).current;

    const onSuggestionsFetchRequested = ({ value }) => {
        debouncedLoadSuggestions(value);
    };

    const getSearchIconProps = inputProps => {
        return {
            fill: inputProps.disabled ? DEFAULT_COLORS.LIGHT : DEFAULT_COLORS.BLACK,
        };
    };

    const renderInputComponent = inputProps => (
        <InputContainer containerWidth={containerWidth}>
            <InputStyled {...inputProps} />
            <IconWrapper>
                <SearchIcon {...getSearchIconProps(inputProps)} />
            </IconWrapper>
        </InputContainer>
    );

    const renderSuggestion = (suggestion, { isHighlighted }) => {
        const suggestionName = get(suggestion, 'name', '');
        return (
            <TypeAheadSuggestion
                containerWidth={containerWidth}
                isHighlighted={isHighlighted}
                suggestion={suggestionName}
            />
        );
    };

    const renderSuggestionsContainer = suggestionsProps => {
        return (
            <TypeAheadContainer
                containerWidth={containerWidth}
                isLoading={isLoadingSuggestions}
                isNoResultFound={isNoSuggestionFound}
                showTypeAheadContainer={showTypeAheadContainer}
                {...suggestionsProps}
            />
        );
    };

    const typeAheadWrapperProps = {
        onMouseLeave: event => {
            setShowTypeAheadContainer(showOnMouseLeaveEvent);
        },
        onMouseEnter: () => {
            setShowTypeAheadContainer(true);
        },
    };

    const autosuggestProps = {
        focusInputOnSuggestionClick: false,
        getSuggestionValue,
        id,
        inputProps: {
            disabled,
            onChange: onChangeInput,
            placeholder,
            value: inputValue,
        },
        onSuggestionSelected,
        onSuggestionsClearRequested,
        onSuggestionsFetchRequested,
        renderSuggestion,
        renderInputComponent,
        renderSuggestionsContainer,
        suggestions,
    };

    return (
        <TypeAheadWrapper {...typeAheadWrapperProps}>
            <Autosuggest {...autosuggestProps} />
        </TypeAheadWrapper>
    );
};

TypeAheadFilter.propTypes = {
    asyncCallback: func,
    containerWidth: string,
    disabled: bool,
    id: oneOfType([number, string]).isRequired,
    name: string.isRequired,
    onChange: func,
    placeholder: string,
    value: object,
    showOnMouseLeaveEvent: bool,
};

TypeAheadFilter.defaultProps = {
    asyncCallback: null,
    containerWidth: '200px',
    disabled: false,
    onChange: () => {},
    placeholder: null,
    value: {},
    showOnMouseLeaveEvent: false,
};

export default TypeAheadFilter;
